import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { ApiError, api, type HomePreferences } from '../api.ts';
import {
  DEFAULT_HOME_PREFERENCES,
  isDefaultHomePreferences,
  normalizeHomePreferences,
} from '../homePrefs.ts';
import {
  BookmarkIcon,
  CheckIcon,
  ClockIcon,
  FolderIcon,
  PlayIcon,
  RefreshIcon,
  SettingsIcon,
  SparklesIcon,
} from '../Icon.tsx';

type PreferenceKey = keyof HomePreferences;

const ROWS: Array<{
  key: PreferenceKey;
  title: string;
  description: string;
  icon: ReactNode;
}> = [
  {
    key: 'showContinue',
    title: 'Devam Et rafını göster',
    description: 'Yarım kalan içerikleri ve sinematik üst bandı ana sayfada tutar.',
    icon: <PlayIcon size={17} />,
  },
  {
    key: 'showRecent',
    title: 'Son eklenenleri göster',
    description: 'Tüm kütüphanelerden en yeni film ve dizileri tek rafta toplar.',
    icon: <SparklesIcon size={17} />,
  },
  {
    key: 'showCollections',
    title: 'Koleksiyon devamını göster',
    description: 'İzlediğiniz filmlerin seri arkadaşlarını ve kalan parçaları önerir.',
    icon: <FolderIcon size={17} />,
  },
  {
    key: 'showHistory',
    title: 'İzleme geçmişini göster',
    description: 'Bitirdiğiniz içerikleri son izleme sırasına göre hatırlatır.',
    icon: <ClockIcon size={17} />,
  },
  {
    key: 'watchlistFirst',
    title: 'Listemi üstte tut',
    description: 'Listem rafını diğer tüm kişisel rafların önüne alır.',
    icon: <BookmarkIcon size={17} />,
  },
];

export function HomePreferencesCard(): ReactNode {
  const [value, setValue] = useState<HomePreferences>(DEFAULT_HOME_PREFERENCES);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .homePreferences()
      .then((result) => {
        if (!cancelled) setValue(normalizeHomePreferences(result));
      })
      .catch(() => {
        if (!cancelled) {
          setValue(DEFAULT_HOME_PREFERENCES);
          setError('Ana sayfa tercihleri okunamadı; varsayılanlar gösteriliyor.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = async (next: HomePreferences): Promise<void> => {
    const previous = value;
    setValue(next);
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setValue(normalizeHomePreferences(await api.updateHomePreferences(next)));
      setNotice('Ana sayfa tercihleri kaydedildi.');
    } catch (caught) {
      setValue(previous);
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Tercihler kaydedilemedi. Bağlantıyı denetleyip yeniden deneyin.',
      );
    } finally {
      setBusy(false);
    }
  };

  const reset = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setValue(normalizeHomePreferences(await api.resetHomePreferences()));
      setNotice('Ana sayfa varsayılan düzene döndü.');
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Varsayılana dönülemedi. Bağlantıyı denetleyip yeniden deneyin.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="account__card account__card--compact" aria-busy={busy}>
      <div className="account__card-header">
        <div className="account__card-icon">
          <SettingsIcon size={20} />
        </div>
        <div>
          <h2 className="account__card-title">Ana Sayfa</h2>
          <p className="account__card-desc">
            Raflar hesabınızı izler; başka bir cihazda da aynı düzen açılır.
          </p>
        </div>
      </div>

      {!loaded ? (
        <p className="account__hint" role="status">
          Ana sayfa tercihleri yükleniyor…
        </p>
      ) : (
        <fieldset className="home-prefs">
          <legend className="visually-hidden">Ana sayfa rafları</legend>
          {ROWS.map((row) => (
            <label key={row.key} className="home-prefs__row">
              <span className="home-prefs__icon" aria-hidden="true">
                {row.icon}
              </span>
              <span className="home-prefs__copy">
                <span className="home-prefs__title">{row.title}</span>
                <span className="home-prefs__desc">{row.description}</span>
              </span>
              <span className="home-prefs__switch">
                <input
                  type="checkbox"
                  className="home-prefs__input"
                  checked={value[row.key]}
                  disabled={busy}
                  onChange={(event) => void persist({ ...value, [row.key]: event.target.checked })}
                />
                <span className="home-prefs__track" aria-hidden="true" />
              </span>
            </label>
          ))}
        </fieldset>
      )}

      {error !== null && (
        <p className="home-prefs__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="home-prefs__notice" role="status">
          <CheckIcon size={13} /> {notice}
        </p>
      )}

      <div className="home-prefs__footer">
        <span className="account__hint">Ses, altyazı ve görüntü tercihleri bu cihazda kalır.</span>
        <button
          type="button"
          className="button button--ghost home-prefs__reset"
          disabled={busy || isDefaultHomePreferences(value)}
          onClick={() => void reset()}
        >
          <RefreshIcon size={14} />
          Varsayılana dön
        </button>
      </div>
    </section>
  );
}
